import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShieldAlert, LogOut, Clock } from "lucide-react";
import { api, tokenStore, orgStore, IMPERSONATION_KEY } from "@/lib/api";
import { toast } from "sonner";

function readState() {
  try {
    const raw = localStorage.getItem(IMPERSONATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function ImpersonationBanner() {
  const [state, setState] = useState(() => readState());

  // Re-read whenever route changes / focus comes back
  useEffect(() => {
    const sync = () => setState(readState());
    window.addEventListener("focus", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("focus", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!state) return null;

  const exit = async () => {
    try { await api.post("/admin/impersonate/exit"); } catch (_) {}
    if (state.realToken) tokenStore.set(state.realToken); else tokenStore.clear();
    if (state.realOrg) orgStore.set(state.realOrg); else orgStore.clear();
    localStorage.removeItem(IMPERSONATION_KEY);
    toast.success("Exited impersonation");
    window.location.href = "/app/admin";
  };

  return (
    <motion.div
      initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="sticky top-0 z-[60] w-full border-b border-amber-500/40 bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500 px-4 py-2.5 text-amber-950 shadow-[0_4px_24px_rgba(217,119,6,0.35)]"
      role="alert"
      data-testid="impersonation-banner"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 flex-shrink-0" />
          <span className="font-medium">
            Impersonating <span className="font-bold">{state.impersonatedEmail}</span> · all actions are logged
          </span>
          <span className="hidden items-center gap-1 rounded-full bg-amber-950/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider sm:inline-flex">
            <Clock className="h-3 w-3" /> 30-min session
          </span>
        </div>
        <button
          onClick={exit}
          className="inline-flex items-center gap-1.5 rounded-full bg-amber-950 px-3 py-1.5 text-xs font-semibold text-amber-100 transition hover:bg-amber-950/85"
          data-testid="impersonation-exit"
        >
          <LogOut className="h-3.5 w-3.5" /> Exit impersonation
        </button>
      </div>
    </motion.div>
  );
}

export function isImpersonating() {
  return !!readState();
}
